import React from 'react';
import { Alert, Box, Button, Chip, CircularProgress, FormControlLabel, Switch, Typography } from '@mui/material';
import { MdSend } from 'react-icons/md';
import ManagedQuoteCard, { isQuoteExpired } from './ManagedQuoteCard';
import { getThirdPartyClient } from '../thirdParty/client';
import type { TpPoint, TpQuote, TpQuoteRequest } from '../thirdParty/types';

interface ThirdPartyRunPanelProps {
  run: any | null;
  /** Pickup point derived from the rooftop; null when it can't be built. */
  pickup: TpPoint | null;
  /** Why the pickup is unusable (missing phone/address) — blocks quoting. */
  pickupProblem?: string | null;
  booking: boolean;
  onBook: (quote: TpQuote, opts?: { requireSignature?: boolean }) => void;
  /** Whether the quote-and-book action renders; the host gates this on the product's manage
   *  capability. Default true. */
  bookAllowed?: boolean;
  /** The instant-quote form rendered in the empty state. */
  instantQuoteSlot?: React.ReactNode;
}

type QuotePhase = 'idle' | 'quoting' | 'quoted';

/** Group draft stops by address for the multi-destination warning. */
const addressGroups = (stops: any[]): { address: string; count: number }[] =>
  stops.map(s => ({ address: s.address || 'Unknown address', count: (s.delivery_orders || []).length }));

/**
 * The HopDrive Managed ⚡ body of the run panel: single-destination constraint →
 * one algorithm-picked price → book. The dealer never chooses a vendor — the
 * selection algorithm does (mock: cheapest; real: server-side strategy) and we
 * serve up ONE price. Couriers do one pickup → one dropoff, so multiple
 * destinations block quoting with a fix-it card.
 */
const ThirdPartyRunPanel: React.FC<ThirdPartyRunPanelProps> = ({
  run,
  pickup,
  pickupProblem,
  booking,
  onBook,
  bookAllowed = true,
  instantQuoteSlot,
}) => {
  const stops: any[] = run?.stops || [];
  const orders = stops.flatMap((s: any) => s.delivery_orders || []);
  const multiDestination = stops.length > 1;
  const hasOrders = orders.length > 0;

  const [rawPhase, setPhase] = React.useState<QuotePhase>('idle');
  const [quote, setQuote] = React.useState<TpQuote | null>(null);
  const [failure, setFailure] = React.useState<string | null>(null);
  const [requireSignature, setRequireSignature] = React.useState(false);

  // Any change to the manifest invalidates the quote (different address/items =
  // different price): the quoted phase is only honored for the signature it was
  // quoted against — derived state, no reset effect.
  const manifestSignature = stops
    .map((s: any) => `${s.address}|${(s.delivery_orders || []).map((o: any) => o.id).join(',')}`)
    .join(';');
  const [quotedSignature, setQuotedSignature] = React.useState<string | null>(null);
  const phase: QuotePhase = rawPhase === 'quoted' && quotedSignature !== manifestSignature ? 'idle' : rawPhase;

  const buildRequest = (): TpQuoteRequest | null => {
    const dest = stops[0];
    if (!dest || !pickup) return null;
    return {
      pickup,
      dropoff: {
        name: dest.recipient_name || dest.recipient_business || 'Recipient',
        business_name: dest.recipient_business || undefined,
        phone: dest.recipient_phone || undefined,
        address: dest.address || '',
        lat: dest.delivery_lat ?? undefined,
        lng: dest.delivery_lng ?? undefined,
      },
      items: orders.map((o: any) => ({
        name: o.item_description || o.part_number || 'Part',
        quantity: o.quantity || 1,
      })),
    };
  };

  const handleGetQuote = async () => {
    const request = buildRequest();
    if (!request) return;
    setPhase('quoting');
    setFailure(null);
    try {
      const result = await getThirdPartyClient().quote(request);
      // The selection "algo" placeholder: cheapest wins. The dealer sees one
      // price; the winning vendor rides the quote into booking internally.
      const best = [...result.quotes].sort((a, b) => a.fee_cents - b.fee_cents)[0] ?? null;
      setQuote(best);
      setQuotedSignature(manifestSignature);
      setPhase('quoted');
      if (!best) setFailure('No courier is available for this delivery right now.');
    } catch (err: any) {
      setPhase('idle');
      setFailure(err?.message || 'Failed to get a quote.');
    }
  };

  const quoteUsable = !!quote && !isQuoteExpired(quote);

  // ── Empty state (no orders selected): instant-quote entry point ──────────
  if (!hasOrders) {
    return (
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant='body2' color='text.secondary'>
          Select orders to send them with HopDrive Managed delivery — or price a one-off delivery without an order.
        </Typography>
        {instantQuoteSlot ?? null}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Pickup problem (missing rooftop phone/address) */}
      {pickupProblem ? (
        <Alert severity='warning' data-testid='pd-tp-pickup-problem'>
          {pickupProblem}
        </Alert>
      ) : null}

      {/* Multi-destination block */}
      {multiDestination ? (
        <Alert severity='warning' data-testid='pd-tp-multidest'>
          <Typography variant='body2' sx={{ fontWeight: 700, mb: 0.5 }}>
            HopDrive Managed delivers to one location per trip
          </Typography>
          <Typography variant='caption' component='div' sx={{ mb: 0.5 }}>
            Your selection spans {stops.length} addresses. Remove orders until one destination remains, or use
            Own Driver for multi-stop runs.
          </Typography>
          {addressGroups(stops).map(g => (
            <Typography key={g.address} variant='caption' component='div' color='text.secondary' noWrap>
              • {g.address} ({g.count} order{g.count !== 1 ? 's' : ''})
            </Typography>
          ))}
        </Alert>
      ) : null}

      {/* Quote area */}
      {phase === 'quoting' ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2, justifyContent: 'center' }}>
          <CircularProgress size={18} />
          <Typography variant='body2' color='text.secondary'>
            Finding the best courier…
          </Typography>
        </Box>
      ) : null}

      {phase === 'quoted' && quote ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid='pd-tp-quotes'>
          <ManagedQuoteCard quote={quote} />
          <Typography variant='caption' color='text.secondary'>
            Final fee may vary slightly — the booked amount shows on the run. Courier photographs
            every dropoff.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                size='small'
                checked={requireSignature}
                onChange={e => setRequireSignature(e.target.checked)}
                disabled={booking}
                data-testid='pd-tp-require-signature'
              />
            }
            label={<Typography variant='body2'>Require recipient signature</Typography>}
            sx={{ ml: 0 }}
          />
        </Box>
      ) : null}

      {failure ? <Alert severity='error'>{failure}</Alert> : null}

      {/* Action */}
      {!bookAllowed ? null : phase !== 'quoted' || !quote ? (
        <Button
          fullWidth variant='contained' size='large'
          onClick={handleGetQuote}
          disabled={multiDestination || !!pickupProblem || phase === 'quoting' || !pickup}
          data-testid='pd-tp-get-quote'
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {multiDestination ? 'Get quote · fix destinations first' : phase === 'quoting' ? 'Getting quote…' : '⚡ Get quote'}
        </Button>
      ) : (
        <Button
          fullWidth variant='contained' size='large'
          onClick={() => quoteUsable && onBook(quote, { requireSignature })}
          disabled={!quoteUsable || booking}
          startIcon={booking ? <CircularProgress size={18} color='inherit' /> : <MdSend />}
          data-testid='pd-tp-book'
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {booking
            ? 'Booking courier…'
            : quoteUsable
              ? `Book delivery · $${(quote.fee_cents / 100).toFixed(2)}`
              : 'Quote expired — re-quote'}
        </Button>
      )}

      {phase === 'quoted' && !booking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Chip label='Re-quote' size='small' variant='outlined' onClick={handleGetQuote} sx={{ cursor: 'pointer' }} />
        </Box>
      ) : null}
    </Box>
  );
};

export default ThirdPartyRunPanel;
